// ─── Portüs Talent Pro — API client (replaces Supabase SDK) ───────────────
// The interface is IDENTICAL to the previous version so no pages need changes.

const API_URL = import.meta.env.VITE_API_URL || '';

// ── Token management ──

function getToken() {
  return localStorage.getItem('portus_token');
}

function setToken(token) {
  localStorage.setItem('portus_token', token);
}

function clearToken() {
  localStorage.removeItem('portus_token');
  localStorage.removeItem('portus_user');
}

function getStoredUser() {
  try {
    const raw = localStorage.getItem('portus_user');
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function setStoredUser(user) {
  localStorage.setItem('portus_user', JSON.stringify(user));
}

// ── Core fetch wrapper ──

async function apiFetch(path, options = {}) {
  const token = getToken();
  const headers = { ...options.headers };

  if (token) headers['Authorization'] = `Bearer ${token}`;

  // Don't set Content-Type for FormData (browser sets it with boundary)
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const res = await fetch(`${API_URL}${path}`, { ...options, headers });

  if (res.status === 401) {
    clearToken();
    // Don't redirect if already on login page
    if (!window.location.pathname.includes('/login')) {
      window.location.href = '/login';
    }
    throw new Error('Sessão expirada');
  }

  if (!res.ok) {
    const text = await res.text();
    let msg;
    try { msg = JSON.parse(text).error; } catch { msg = text; }
    throw new Error(msg || `HTTP ${res.status}`);
  }

  // Handle empty responses (204 No Content)
  if (res.status === 204) return undefined;

  // Support arraybuffer response type
  if (options.responseType === 'arraybuffer') {
    return res.arrayBuffer();
  }

  return res.json();
}

// ── Entity shim ──
// Same interface: db.entities.X.list/filter/get/create/update/delete

const FIELD_MAP = {
  created_date: 'created_at',
  updated_date: 'updated_at',
};

function normalizeField(field) {
  if (!field) return field;
  const descending = field.startsWith('-');
  const name = descending ? field.slice(1) : field;
  const mapped = FIELD_MAP[name] ?? name;
  return descending ? `-${mapped}` : mapped;
}

function createEntityShim(tableName) {
  return {
    async list(orderBy) {
      const params = new URLSearchParams();
      if (orderBy) params.set('orderBy', normalizeField(orderBy));
      const qs = params.toString();
      return apiFetch(`/api/entities/${tableName}${qs ? '?' + qs : ''}`);
    },

    async filter(filters = {}, orderBy, limit) {
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(filters)) {
        if (value !== undefined && value !== null) {
          params.set(key, value);
        }
      }
      if (orderBy) params.set('orderBy', normalizeField(orderBy));
      if (limit) params.set('limit', limit);
      const qs = params.toString();
      return apiFetch(`/api/entities/${tableName}${qs ? '?' + qs : ''}`);
    },

    async get(id) {
      return apiFetch(`/api/entities/${tableName}/${id}`);
    },

    async create(record) {
      return apiFetch(`/api/entities/${tableName}`, {
        method: 'POST',
        body: JSON.stringify(record),
      });
    },

    async update(id, updates) {
      return apiFetch(`/api/entities/${tableName}/${id}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      });
    },

    async delete(id) {
      return apiFetch(`/api/entities/${tableName}/${id}`, {
        method: 'DELETE',
      });
    },
  };
}

// ── Auth shim ──

const auth = {
  async login(email, password) {
    const result = await apiFetch('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    setToken(result.token);
    setStoredUser(result.user);
    return result;
  },

  async register(email, password, full_name) {
    const result = await apiFetch('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, full_name }),
    });
    setToken(result.token);
    setStoredUser(result.user);
    return result;
  },

  async me() {
    const token = getToken();
    if (!token) return null;
    try {
      const user = await apiFetch('/api/auth/me');
      setStoredUser(user);
      return user;
    } catch {
      return null;
    }
  },

  async logout() {
    clearToken();
    window.location.href = '/login';
  },

  redirectToLogin() {
    window.location.href = '/login';
  },
};

// ── Functions shim ──
// Matches Base44's return shape: { data: ... }

const functions = {
  async invoke(name, payload, options) {
    const fetchOptions = {
      method: 'POST',
      body: JSON.stringify(payload),
    };

    if (options?.responseType === 'arraybuffer') {
      fetchOptions.responseType = 'arraybuffer';
      const data = await apiFetch(`/api/functions/${name}`, fetchOptions);
      return { data };
    }

    const data = await apiFetch(`/api/functions/${name}`, fetchOptions);
    return { data };
  },
};

// ── Storage helper ──
// Used by pages that upload files (portfolio, admin lessons, marketplace)

export async function uploadFile(file, bucket = 'portfolio-assets') {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('bucket', bucket);

  const result = await apiFetch('/api/storage/upload', {
    method: 'POST',
    body: formData,
  });

  return result.publicUrl;
}

// ── Portfolio helpers ──
// Replaces direct supabase.from("portfolios") calls

export async function getMyPortfolio() {
  return apiFetch('/api/portfolio/mine');
}

export async function saveMyPortfolio(payload) {
  return apiFetch('/api/portfolio/mine', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function getPublicPortfolio(slug) {
  return apiFetch(`/api/portfolio/${slug}`);
}

// ── Opportunities & Applications ──

export async function getOpportunityFilters() {
  return apiFetch('/api/opportunities/filters');
}

export async function listOpportunities(filters = {}) {
  const params = new URLSearchParams();
  if (filters.search) params.set('search', filters.search);
  if (filters.page) params.set('page', filters.page);
  if (filters.pageSize) params.set('pageSize', filters.pageSize);
  if (filters.states?.length) params.set('states', filters.states.join(','));
  if (filters.cities?.length) params.set('cities', filters.cities.join(','));
  if (filters.jobModelIds?.length) params.set('jobModelIds', filters.jobModelIds.join(','));
  if (filters.contractModelIds?.length) params.set('contractModelIds', filters.contractModelIds.join(','));
  if (filters.jobAvailabilityIds?.length) params.set('jobAvailabilityIds', filters.jobAvailabilityIds.join(','));
  if (filters.jobPositionIds?.length) params.set('jobPositionIds', filters.jobPositionIds.join(','));
  if (filters.seniorities?.length) params.set('seniorities', filters.seniorities.join(','));
  if (filters.minSalary) params.set('minSalary', filters.minSalary);
  if (filters.maxSalary) params.set('maxSalary', filters.maxSalary);
  const qs = params.toString();
  return apiFetch(`/api/opportunities${qs ? '?' + qs : ''}`);
}

export async function getOpportunity(id) {
  return apiFetch(`/api/opportunities/${id}`);
}

export async function applyToOpportunity(id, additionalQuestions = []) {
  return apiFetch(`/api/opportunities/${id}/apply`, {
    method: 'POST',
    body: JSON.stringify({ additionalQuestions }),
  });
}

export async function cancelApplication(jobOpeningId) {
  return apiFetch(`/api/opportunities/${jobOpeningId}/cancel`, {
    method: 'POST',
  });
}

export async function listApplications(filters = {}) {
  const params = new URLSearchParams();
  if (filters.search) params.set('search', filters.search);
  if (filters.page) params.set('page', filters.page);
  if (filters.pageSize) params.set('pageSize', filters.pageSize);
  if (filters.status?.length) params.set('status', filters.status.join(','));
  const qs = params.toString();
  return apiFetch(`/api/opportunities/applications/mine${qs ? '?' + qs : ''}`);
}

// ── Workshops / Oficinas ──

export const workshops = {
  async book(lessonId) {
    return apiFetch(`/api/workshops/${lessonId}/book`, { method: 'POST' });
  },
  async cancel(lessonId) {
    return apiFetch(`/api/workshops/${lessonId}/book`, { method: 'DELETE' });
  },
  async myBookings() {
    return apiFetch('/api/workshops/my-bookings');
  },
  async availability(lessonId) {
    return apiFetch(`/api/workshops/${lessonId}/availability`);
  },
  async participants(lessonId) {
    return apiFetch(`/api/workshops/${lessonId}/bookings`);
  },
  async sendReminder(lessonId) {
    return apiFetch(`/api/workshops/${lessonId}/send-reminder`, { method: 'POST' });
  },
};

// ── App logs shim (no-op) ──

const appLogs = {
  logUserInApp: () => Promise.resolve(),
};

// ── Backward compat: `supabase` export ──
// Some pages import { supabase } from db.js for storage or direct queries.
// This shim redirects those calls to the API.

export const supabase = {
  auth: {
    async getUser() {
      const user = await auth.me();
      if (!user) return { data: { user: null }, error: new Error('Not authenticated') };
      return {
        data: {
          user: {
            id: user.id,
            email: user.email,
            user_metadata: {
              full_name: user.full_name,
              role: user.role,
            },
            created_at: user.created_at,
          },
        },
        error: null,
      };
    },
    async getSession() {
      const token = getToken();
      const user = getStoredUser();
      if (!token || !user) return { data: { session: null } };
      return { data: { session: { access_token: token, user } } };
    },
    async signInWithPassword({ email, password }) {
      try {
        await auth.login(email, password);
        return { error: null };
      } catch (err) {
        return { error: err };
      }
    },
    async signOut() {
      clearToken();
    },
    onAuthStateChange(callback) {
      // Check initial state
      const token = getToken();
      const user = getStoredUser();
      if (token && user) {
        callback('SIGNED_IN', {
          access_token: token,
          user: {
            id: user.id,
            email: user.email,
            user_metadata: { full_name: user.full_name, role: user.role },
            created_at: user.created_at,
          },
        });
      }
      // Return a subscription-like object for cleanup
      return { data: { subscription: { unsubscribe: () => {} } } };
    },
  },
  storage: {
    from(bucket) {
      return {
        async upload(path, file, _options) {
          try {
            await uploadFile(file, bucket);
            return { error: null };
          } catch (error) {
            return { error };
          }
        },
        getPublicUrl(_path) {
          // This is called after upload — the new API returns the URL directly from upload.
          // For backward compat, return a placeholder. Callers should use uploadFile() instead.
          return { data: { publicUrl: '' } };
        },
      };
    },
  },
  from(table) {
    const shim = createEntityShim(table);
    // Chainable query builder to support: supabase.from("x").select("*").eq("id", 1).single()
    return {
      select(_cols) {
        let filters = {};
        let isSingle = false;
        const builder = {
          eq(col, val) { filters[col] = val; return builder; },
          is(col, val) { filters[col] = val; return builder; },
          single() { isSingle = true; return builder; },
          async then(resolve, reject) {
            try {
              const data = await shim.filter(filters);
              if (isSingle) {
                resolve({ data: data[0] ?? null, error: data.length === 0 ? { message: 'Not found' } : null });
              } else {
                resolve({ data, error: null });
              }
            } catch (error) {
              if (reject) reject(error);
              else resolve({ data: null, error });
            }
          },
        };
        return builder;
      },
      insert(record) {
        return {
          select() {
            return {
              single() {
                return {
                  async then(resolve, reject) {
                    try {
                      const data = await shim.create(record);
                      resolve({ data, error: null });
                    } catch (error) {
                      if (reject) reject(error);
                      else resolve({ data: null, error });
                    }
                  },
                };
              },
              async then(resolve, reject) {
                try {
                  const data = await shim.create(record);
                  resolve({ data, error: null });
                } catch (error) {
                  if (reject) reject(error);
                  else resolve({ data: null, error });
                }
              },
            };
          },
          async then(resolve, reject) {
            try {
              await shim.create(record);
              resolve({ error: null });
            } catch (error) {
              if (reject) reject(error);
              else resolve({ error });
            }
          },
        };
      },
      upsert(record, _options) {
        // For portfolio upsert — delegate to saveMyPortfolio
        return {
          async then(resolve, reject) {
            try {
              await saveMyPortfolio(record);
              resolve({ error: null });
            } catch (error) {
              if (reject) reject(error);
              else resolve({ error });
            }
          },
        };
      },
      update(updates) {
        let filters = {};
        const builder = {
          eq(col, val) { filters[col] = val; return builder; },
          select() {
            return {
              single() {
                return {
                  async then(resolve, reject) {
                    try {
                      const id = filters.id;
                      const data = await shim.update(id, updates);
                      resolve({ data, error: null });
                    } catch (error) {
                      if (reject) reject(error);
                      else resolve({ data: null, error });
                    }
                  },
                };
              },
            };
          },
          async then(resolve, reject) {
            try {
              const id = filters.id;
              await shim.update(id, updates);
              resolve({ error: null });
            } catch (error) {
              if (reject) reject(error);
              else resolve({ error });
            }
          },
        };
        return builder;
      },
      delete() {
        let filters = {};
        const builder = {
          eq(col, val) { filters[col] = val; return builder; },
          async then(resolve, reject) {
            try {
              const id = filters.id;
              await shim.delete(id);
              resolve({ error: null });
            } catch (error) {
              if (reject) reject(error);
              else resolve({ error });
            }
          },
        };
        return builder;
      },
    };
  },
};

// ── Main export ──

// ── Profile (candidate profile via /api/profile/me) ──

const profile = {
  async get() {
    return apiFetch('/api/profile/me');
  },
  async update(data) {
    return apiFetch('/api/profile/me', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  async uploadAvatar(file) {
    const formData = new FormData();
    formData.append('file', file);
    return apiFetch('/api/profile/avatar', {
      method: 'POST',
      body: formData,
    });
  },
};

// ── Admin endpoints ──

const admin = {
  async users(params = {}) {
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', params.page);
    if (params.search) qs.set('search', params.search);
    if (params.subscription) qs.set('subscription', params.subscription);
    if (params.test) qs.set('test', params.test);
    if (params.orderBy) qs.set('orderBy', params.orderBy);
    const query = qs.toString();
    return apiFetch(`/api/admin/users${query ? '?' + query : ''}`);
  },
  async stats() {
    return apiFetch('/api/admin/users/stats');
  },
  async userDetail(id) {
    return apiFetch(`/api/admin/users/${id}`);
  },
  async lookupEmail(email) {
    return apiFetch(`/api/admin/users/lookup?email=${encodeURIComponent(email)}`);
  },
  async createUser(data) {
    return apiFetch('/api/admin/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

export const db = {
  auth,
  profile,
  admin,
  entities: {
    CareerTrack: createEntityShim('career_tracks'),
    Level: createEntityShim('levels'),
    Lesson: createEntityShim('lessons'),
    UserProfile: createEntityShim('user_profiles'),
    UserProgress: createEntityShim('user_progress'),
    Exam: createEntityShim('exams'),
    ExamAttempt: createEntityShim('exam_attempts'),
    Certificate: createEntityShim('certificates'),
    PracticalProject: createEntityShim('practical_projects'),
    ProjectSubmission: createEntityShim('project_submissions'),
    ProjectEvaluation: createEntityShim('project_evaluations'),
    InterviewSession: createEntityShim('interview_sessions'),
    OnboardingProfile: createEntityShim('onboarding_profiles'),
    MarketplaceCourse: createEntityShim('marketplace_courses'),
    PartnerApplication: createEntityShim('partner_applications'),
    JobListing: createEntityShim('job_listings'),
    Subscription: createEntityShim('subscriptions'),
  },
  functions,
  appLogs,
};
